import { InfoIcon } from '@chakra-ui/icons';
import { Box, Flex, Badge, useToast, Spacer } from '@chakra-ui/react';
import React from 'react';

import { ArtifactIdChip } from './ArtifactIdChip';
import { EditModeControls } from './EditModeControls';
import { TapeBuilderMode } from './GraphContext';
import { KeyboardShortcutsTooltip } from './KeyboardShortCutsTooltip';
import { TapeBuilderPipelinePicker } from './pipelinePicker/TapeBuilderPipelinePicker';
import { TapeBuilderDiffDocument } from './TapeBuilderDiffDocument';
import { TapeBuilderModeSelect } from './TapeBuilderModeSelect';
import { defaultToastOptions } from './utils';
import { ViewModeControls } from './ViewModeControls';
import { GetBorrowingBasePipelineStatusQueryGQLType } from 'packages/tapeBuilder/requests/tapeBuilder.gql.generated';
import { FiRefreshCw } from 'react-icons/fi';
import {
  IconButton,
  IconButtonV2,
} from '~/packages/common-frontend/components/IconButton';
import { useCommitArtifactDocumentAndCreditAgreementsForPipelineDefinitionMutation } from '~/packages/creditAgreements/requests/requests.gql.generated';

type RenderStatusProps = {
  pipelineDefinitionId: string;
  status: GetBorrowingBasePipelineStatusQueryGQLType;
  dirty: boolean;
  updateStatus: () => void;
};
// The commit action (when the saved WIP still needs committing) plus the
// "latest committed" reference chip. Rendered inline in the status row's
// right-hand cluster.
export const RenderStatus = ({
  pipelineDefinitionId,
  status,
  dirty,
  updateStatus,
}: RenderStatusProps) => {
  const toast = useToast(defaultToastOptions);

  const {
    latestArtifactDocumentId,
    latestArtifactDocumentVersion,
    wipNeedsCommit,
  } = status.borrowingBasePipelineStatus;

  const [
    commitArtifactDocumentAndCreditAgreementsMutation,
    { loading: updatingCAs },
  ] = useCommitArtifactDocumentAndCreditAgreementsForPipelineDefinitionMutation(
    {
      onCompleted: ({ commitArtifactDocumentAndCreditAgreements: result }) => {
        result.successes.forEach((success) => {
          toast({
            title: success,
            position: 'top',
            status: 'success',
            duration: null, // this is important information, do not auto-close.
          });
        });

        result.errors.forEach((errors) => {
          toast({
            title: errors,
            position: 'top',
            status: 'error',
            duration: null, // this is important information, do not auto-close.
          });
        });

        // need to reset status after commit
        updateStatus();
      },
      onError: (error) => {
        // eslint-disable-next-line no-console
        const message = `${error}`;
        toast({
          id: `${Date.now()}`,
          title: message,
          status: 'error',
          duration: null, // errors are important, do not auto-close
        });
      },
    },
  );
  return (
    <>
      {!dirty && wipNeedsCommit && (
        // should only be able to "commit" after a save.
        // if graph is "dirty", it should be saved first as wip, prior to committing (for uber-commit)
        <Badge
          cursor="pointer"
          variant="solid"
          colorScheme="red"
          onClick={() =>
            commitArtifactDocumentAndCreditAgreementsMutation({
              variables: { pipelineDefinitionId },
            })
          }
        >
          COMMIT TAPE-BUILDER AND UPDATE CAs
        </Badge>
      )}
      {updatingCAs && <Badge colorScheme="yellow">Updating CAs...</Badge>}
      <ArtifactIdChip
        id={latestArtifactDocumentId}
        label="Latest committed"
        version={latestArtifactDocumentVersion}
      />
    </>
  );
};

type TapeBuilderStatusProps = {
  dirty: boolean;
  dbMessage: string | null;
  status: GetBorrowingBasePipelineStatusQueryGQLType | undefined;
  pipelineDefinitionId: string;
  mode: TapeBuilderMode;
  setMode: (mode: TapeBuilderMode) => void;
  onSaveWip: () => void;
  updateStatus: () => void;
  diff: TapeBuilderDiffDocument | null;
  setDiff: (diff: TapeBuilderDiffDocument | null) => void;
  setArtifactDocumentId: (artifactDocumentId: string) => void;
  onLoadArtifactInstances: (pipelineRunId: string) => void;
  // Edit mode controls
  onClearGraph?: () => void;
  onResetToLatest?: () => void;
  onUndoChanges?: () => void;
  isGraphFiltered?: boolean;
};
export const TapeBuilderStatus = ({
  dirty,
  status,
  pipelineDefinitionId,
  dbMessage,
  mode,
  onSaveWip,
  updateStatus,
  diff,
  setDiff,
  onLoadArtifactInstances,
  setArtifactDocumentId,
  setMode,
  onClearGraph,
  onResetToLatest,
  onUndoChanges,
  isGraphFiltered = false,
}: TapeBuilderStatusProps) => {
  return (
    <Box width="100%" id="tape-builder-status-bar">
      {/* Mode tab strip — sits above the toolbar rectangle (mode switches only
          when the graph is not dirty). */}
      <TapeBuilderModeSelect mode={mode} setMode={setMode} disabled={dirty} />

      {/* The toolbar for the active mode: contextual controls on the left,
          transient state + global actions on the right. */}
      <Flex
        border="1px solid #ccc"
        borderRadius="md"
        minWidth="max-content"
        alignItems="center"
        gap="2"
        px="2"
        py="1"
      >
        {mode === 'Edit' &&
          onClearGraph &&
          onResetToLatest &&
          onUndoChanges && (
            <EditModeControls
              onClearGraph={onClearGraph}
              onResetToLatest={onResetToLatest}
              onUndoChanges={onUndoChanges}
              filtered={isGraphFiltered}
            />
          )}
        {mode === 'Monitor' && (
          <TapeBuilderPipelinePicker
            pipelineDefinitionId={pipelineDefinitionId}
            loadArtifactInstances={onLoadArtifactInstances}
          />
        )}
        {mode === 'View' && (
          <ViewModeControls
            pipelineDefinitionId={pipelineDefinitionId}
            onDocumentSelected={(v) => setArtifactDocumentId(v)}
            diff={diff}
            setDiff={setDiff}
          />
        )}

        <Spacer />

        {dbMessage && (
          <Badge variant="outline" colorScheme="yellow">
            {dbMessage}
          </Badge>
        )}
        {dirty && (
          <Badge
            cursor="pointer"
            variant="solid"
            colorScheme="red"
            onClick={() => onSaveWip()}
          >
            Unsaved. Click to Save
          </Badge>
        )}
        {status && (
          <RenderStatus
            pipelineDefinitionId={pipelineDefinitionId}
            status={status}
            dirty={dirty}
            updateStatus={updateStatus}
          />
        )}
        <IconButtonV2
          icon={<InfoIcon />}
          aria-label="Available Keyboard Shortcuts"
          variant="v2-ghost"
          label={<KeyboardShortcutsTooltip />}
          placement="bottom-end"
        />
        <IconButton
          label="Refresh"
          ariaLabel="Refresh"
          icon={<FiRefreshCw />}
          variant="outline"
          onClick={updateStatus}
        />
      </Flex>
    </Box>
  );
};
